import React, { useState, useMemo } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex,
    Modal, Segmented, Avatar, List, Empty, Breadcrumb, Tooltip, Badge
} from "antd";
import {
    BookOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined,
    EnvironmentOutlined, GlobalOutlined, UserOutlined, TeamOutlined,
    PhoneOutlined, MailOutlined, QuestionCircleOutlined, CloseCircleOutlined,
    ArrowLeftOutlined, ReadOutlined, CrownOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { ScheduledSessionDto, ScheduledSessionPlayerDto } from "../../commons/data/types";
import { createGoogleCalendarUrl } from "../../commons/calendarUtils";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;

export const SessionsPage: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn>
                <SessionsContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

type SessionFilter = 'UPCOMING' | 'RECENT' | 'ALL';

const SessionsContent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [filter, setFilter] = useState<SessionFilter>('UPCOMING');
    const [selectedSession, setSelectedSession] = useState<ScheduledSessionDto | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const { data: sessions = [], isLoading } = useQuery<ScheduledSessionDto[]>(
        'my-managed-sessions',
        () => axios.get('/api/launcher/my/sessions').then(res => res.data)
    );

    const now = Date.now();

    const filteredSessions = useMemo(() => {
        if (filter === 'UPCOMING') {
            return sessions.filter(s => s.sessionTime >= now);
        }
        if (filter === 'RECENT') {
            return sessions.filter(s => s.sessionTime < now);
        }
        return sessions;
    }, [sessions, filter, now]);

    const upcomingCount = useMemo(() => sessions.filter(s => s.sessionTime >= now).length, [sessions, now]);
    const recentCount = useMemo(() => sessions.filter(s => s.sessionTime < now).length, [sessions, now]);

    const handleCardClick = (session: ScheduledSessionDto) => {
        setSelectedSession(session);
        setModalOpen(true);
    };

    return (
        <div style={{ maxWidth: '1100px', margin: '30px auto', padding: '0 20px' }}>
            {/* BREADCRUMB & HEADER NAV */}
            <div style={{ marginBottom: 16 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="small">
                    <Breadcrumb
                        items={[
                            { title: <Link to="/">{t('home.title', 'Home')}</Link> },
                            { title: <Link to="/profile">{t('profile.title', 'Profile')}</Link> },
                            { title: t('sessions.page.title', 'Scheduled Sessions') }
                        ]}
                    />
                    <Space wrap>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/profile')}>
                            {t('profile.backToProfile', 'Back to Profile')}
                        </Button>
                    </Space>
                </Flex>
            </div>

            {/* HEADER HERO */}
            <Card style={{ ...launcherCard, marginBottom: 24 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            <CalendarOutlined style={{ marginRight: 10, color: '#1890ff' }} />
                            {t('sessions.page.title', 'Scheduled Sessions')}
                        </Title>
                        <Paragraph type="secondary" style={{ margin: '6px 0 0', fontSize: 14 }}>
                            {t('sessions.page.description', 'View all upcoming and recent game sessions where you are the Game Master or Organizer, manage rosters, and check player RSVPs.')}
                        </Paragraph>
                    </div>

                    <Segmented
                        value={filter}
                        onChange={(val) => setFilter(val as SessionFilter)}
                        size="large"
                        options={[
                            {
                                label: `${t('sessions.filter.upcoming', 'Upcoming')} (${upcomingCount})`,
                                value: 'UPCOMING',
                                icon: <ClockCircleOutlined />
                            },
                            {
                                label: `${t('sessions.filter.recent', 'Recent')} (${recentCount})`,
                                value: 'RECENT',
                                icon: <CheckCircleOutlined />
                            },
                            {
                                label: `${t('sessions.filter.all', 'All')} (${sessions.length})`,
                                value: 'ALL'
                            }
                        ]}
                    />
                </Flex>
            </Card>

            {/* CONTENT */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '80px 0' }}>
                    <Spin size="large" />
                </div>
            ) : filteredSessions.length === 0 ? (
                <Card style={{ ...launcherCard, textAlign: 'center', padding: '50px 20px' }}>
                    <Empty
                        description={
                            <div>
                                <Title level={4} style={{ marginBottom: 8 }}>
                                    {filter === 'UPCOMING'
                                        ? t('sessions.empty.upcomingTitle', 'No Upcoming Scheduled Sessions')
                                        : filter === 'RECENT'
                                        ? t('sessions.empty.recentTitle', 'No Recent Sessions')
                                        : t('sessions.empty.allTitle', 'No Scheduled Sessions Found')}
                                </Title>
                                <Text type="secondary">
                                    {t('sessions.empty.desc', 'When an adventure invitation is scheduled, its sessions and confirmed player reservations will appear here.')}
                                </Text>
                            </div>
                        }
                    >
                        <Space style={{ marginTop: 16 }}>
                            <Button type="primary" icon={<BookOutlined />} onClick={() => navigate('/organizer')}>
                                {t('organizer.home.title', 'Organizer Home')}
                            </Button>
                        </Space>
                    </Empty>
                </Card>
            ) : (
                <Row gutter={[20, 20]}>
                    {filteredSessions.map((session) => (
                        <Col xs={24} sm={24} md={12} lg={12} key={session.sessionId}>
                            <SessionCard session={session} onClick={() => handleCardClick(session)} />
                        </Col>
                    ))}
                </Row>
            )}

            {/* ROSTER MODAL */}
            {selectedSession && (
                <SessionRosterModal
                    session={selectedSession}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </div>
    );
};

interface SessionCardProps {
    session: ScheduledSessionDto;
    onClick: () => void;
}

const SessionCard: React.FC<SessionCardProps> = ({ session, onClick }) => {
    const { t } = useTranslation();
    const isPast = session.sessionTime < Date.now();

    const calendarUrl = session.sessionTime
        ? createGoogleCalendarUrl({
            title: session.adventureTitle || 'Adventure Session',
            sessionTime: session.sessionTime,
            systemTitle: session.systemTitle,
            gmName: session.gmName,
            organizerName: session.organizerName,
            invitationId: session.invitationId,
            online: session.online,
            onlinePlatform: session.onlinePlatform,
            addressGmapsFormatted: session.addressGmapsFormatted,
            addressGmapsUrl: session.addressGmapsUrl,
            accessInfo: session.accessInfo
        })
        : null;

    return (
        <Card
            hoverable
            style={{
                ...launcherCard,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 12,
                overflow: 'hidden',
                border: isPast ? '1px solid #e8e8e8' : '1px solid #91caff'
            }}
            bodyStyle={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                flex: 1
            }}
            onClick={onClick}
        >
            {/* CARD TOP BAR: ADVENTURE & ROLES */}
            <Flex justify="space-between" align="flex-start" gap="small" style={{ marginBottom: 12 }}>
                <Space wrap size={[4, 6]}>
                    {session.isUserGm && (
                        <Tag color="gold" icon={<CrownOutlined />} style={{ fontWeight: 600 }}>
                            {t('sessions.role.gm', 'Game Master')}
                        </Tag>
                    )}
                    {session.isUserOrganizer && (
                        <Tag color="blue" icon={<ReadOutlined />} style={{ fontWeight: 600 }}>
                            {t('sessions.role.organizer', 'Organizer')}
                        </Tag>
                    )}
                    {session.isUserPlayer && !session.isUserGm && (
                        <Tag color="green" icon={<UserOutlined />} style={{ fontWeight: 600 }}>
                            {t('sessions.role.player', 'Player')}
                        </Tag>
                    )}
                    {session.systemTitle && (
                        <Tag color="purple" icon={<BookOutlined />}>
                            {session.systemTitle}
                        </Tag>
                    )}
                </Space>

                <Tag color={isPast ? 'default' : 'green'} style={{ fontWeight: 600 }}>
                    {isPast ? t('sessions.status.completed', 'Past') : t('sessions.status.scheduled', 'Scheduled')}
                </Tag>
            </Flex>

            {/* MAIN CONTENT: COVER & ADVENTURE TITLE */}
            <Row gutter={[16, 12]} align="middle" style={{ marginBottom: 16 }}>
                {session.adventureCoverUrl && (
                    <Col xs={6} sm={5}>
                        <img
                            src={session.adventureCoverUrl}
                            alt={session.adventureTitle || ''}
                            style={{ width: '100%', maxHeight: 85, objectFit: 'cover', borderRadius: 8 }}
                        />
                    </Col>
                )}
                <Col xs={session.adventureCoverUrl ? 18 : 24} sm={session.adventureCoverUrl ? 19 : 24}>
                    <Title level={4} style={{ margin: 0, fontSize: 16, lineHeight: 1.3 }}>
                        {session.adventureTitle}
                    </Title>
                    {session.organizerName && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 3 }}>
                            {session.organizerName}
                        </Text>
                    )}
                </Col>
            </Row>

            {/* DATE & TIME */}
            <div style={{
                background: isPast ? '#f5f5f5' : '#e6f4ff',
                padding: '10px 14px',
                borderRadius: 8,
                marginBottom: 14
            }}>
                <Flex align="center" gap="small">
                    <CalendarOutlined style={{ fontSize: 16, color: isPast ? '#888' : '#1890ff' }} />
                    <Text strong style={{ fontSize: 14, color: isPast ? '#555' : '#0958d9' }}>
                        {dayjs(session.sessionTime).format('dddd, MMMM D, YYYY [at] HH:mm')}
                    </Text>
                </Flex>
            </div>

            {/* LOCATION INFO */}
            <div style={{ marginBottom: 14, fontSize: 13, color: '#666' }}>
                {session.online ? (
                    <Flex align="center" gap="small">
                        <GlobalOutlined style={{ color: '#722ed1' }} />
                        <Text>{session.onlinePlatform ? `Online - ${session.onlinePlatform}` : 'Online'}</Text>
                    </Flex>
                ) : (
                    <Flex align="flex-start" gap="small">
                        <EnvironmentOutlined style={{ color: '#ff4d4f', marginTop: 3 }} />
                        <Text ellipsis style={{ maxWidth: '90%' }}>
                            {session.addressGmapsFormatted || session.addressGmapsUrl || t('invitations.detail.physical', 'Physical Venue')}
                        </Text>
                    </Flex>
                )}
            </div>

            {/* FOOTER: PLAYER QUORUM & BUTTONS */}
            <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="small">
                    <Badge
                        count={`${session.confirmedPlayersCount} / ${session.maxTotalPlayers} ${t('sessions.players', 'Players')}`}
                        style={{
                            backgroundColor: session.confirmedPlayersCount >= session.maxTotalPlayers ? '#52c41a' : '#1890ff',
                            fontSize: 12,
                            padding: '0 8px'
                        }}
                    />

                    <Space onClick={(e) => e.stopPropagation()}>
                        {calendarUrl && (
                            <Tooltip title={t('joinRequests.detail.addToCalendarTooltip', 'Add this session to your Google Calendar')}>
                                <Button
                                    size="small"
                                    icon={<CalendarOutlined />}
                                    href={calendarUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('common.calendar', 'Calendar')}
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            type="primary"
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={onClick}
                        >
                            {t('sessions.viewRoster', 'Roster')}
                        </Button>
                    </Space>
                </Flex>
            </div>
        </Card>
    );
};

interface SessionRosterModalProps {
    session: ScheduledSessionDto;
    open: boolean;
    onClose: () => void;
}

const SessionRosterModal: React.FC<SessionRosterModalProps> = ({ session, open, onClose }) => {
    const { t } = useTranslation();

    const calendarUrl = session.sessionTime
        ? createGoogleCalendarUrl({
            title: session.adventureTitle || 'Adventure Session',
            sessionTime: session.sessionTime,
            systemTitle: session.systemTitle,
            gmName: session.gmName,
            organizerName: session.organizerName,
            invitationId: session.invitationId,
            online: session.online,
            onlinePlatform: session.onlinePlatform,
            addressGmapsFormatted: session.addressGmapsFormatted,
            addressGmapsUrl: session.addressGmapsUrl,
            accessInfo: session.accessInfo
        })
        : null;

    return (
        <Modal
            title={
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        {session.adventureTitle}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {dayjs(session.sessionTime).format('dddd, MMMM D, YYYY [at] HH:mm')}
                    </Text>
                </div>
            }
            open={open}
            onCancel={onClose}
            width={720}
            footer={[
                calendarUrl && (
                    <Button
                        key="cal"
                        icon={<CalendarOutlined />}
                        href={calendarUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('joinRequests.detail.addToCalendar', 'Google Calendar')}
                    </Button>
                ),
                <Button key="close" type="primary" onClick={onClose}>
                    {t('common.close', 'Close')}
                </Button>
            ]}
        >
            {/* SESSION QUICK INFO */}
            <div style={{
                background: '#fafafa',
                padding: '14px 18px',
                borderRadius: 8,
                marginBottom: 20,
                border: '1px solid #f0f0f0'
            }}>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12}>
                        <Text type="secondary">{t('invitations.system', 'System')}: </Text>
                        <Text strong>{session.systemTitle || '—'}</Text>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Text type="secondary">{t('invitations.gm', 'Game Master')}: </Text>
                        <Text strong>{session.gmName || '—'}</Text>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Text type="secondary">{t('invitations.organizer', 'Organizer')}: </Text>
                        <Text strong>{session.organizerName || '—'}</Text>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Text type="secondary">{t('invitations.detail.location', 'Location')}: </Text>
                        {session.online ? (
                            <Text strong>{session.onlinePlatform ? `Online (${session.onlinePlatform})` : 'Online'}</Text>
                        ) : (
                            <Text strong>
                                {session.addressGmapsUrl ? (
                                    <a href={session.addressGmapsUrl} target="_blank" rel="noopener noreferrer">
                                        {session.addressGmapsFormatted || t('invitations.detail.physical', 'Physical Venue')}
                                    </a>
                                ) : (
                                    session.addressGmapsFormatted || '—'
                                )}
                            </Text>
                        )}
                    </Col>
                </Row>
            </div>

            {/* PLAYERS LIST */}
            <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>
                    {t('sessions.roster.title', 'Confirmed Player Reservations')} ({session.players.length} / {session.maxTotalPlayers})
                </Title>
            </Flex>

            {session.players.length === 0 ? (
                <Empty description={t('sessions.roster.empty', 'No players have reserved seats for this session yet.')} />
            ) : (
                <List
                    itemLayout="vertical"
                    dataSource={session.players}
                    renderItem={(player: ScheduledSessionPlayerDto) => (
                        <List.Item
                            key={player.reservationId || player.playerId}
                            style={{
                                padding: '12px 16px',
                                background: '#fff',
                                border: '1px solid #f0f0f0',
                                borderRadius: 8,
                                marginBottom: 10
                            }}
                        >
                            <Flex justify="space-between" align="flex-start" wrap="wrap" gap="small">
                                <Space align="center" size="middle">
                                    <Avatar
                                        src={player.playerPicture}
                                        icon={<UserOutlined />}
                                        size={44}
                                        style={{ backgroundColor: '#1890ff' }}
                                    />
                                    <div>
                                        <Space>
                                            <Text strong style={{ fontSize: 15 }}>{player.playerName}</Text>
                                            {player.playerNickname && (
                                                <Text type="secondary">({player.playerNickname})</Text>
                                            )}
                                        </Space>
                                        <div style={{ marginTop: 2 }}>
                                            <Space size="middle" wrap style={{ fontSize: 12, color: '#666' }}>
                                                {player.email && (
                                                    <Flex align="center" gap={4}>
                                                        <MailOutlined />
                                                        <a href={`mailto:${player.email}`}>{player.email}</a>
                                                    </Flex>
                                                )}
                                                {player.cellphone && (
                                                    <Flex align="center" gap={4}>
                                                        <PhoneOutlined />
                                                        <a href={`tel:${player.cellphone}`}>{player.cellphone}</a>
                                                    </Flex>
                                                )}
                                            </Space>
                                        </div>
                                    </div>
                                </Space>

                                <Space wrap>
                                    {player.rsvpChoice === 'YES' && (
                                        <Tag color="green" icon={<CheckCircleOutlined />}>
                                            {t('invitations.rsvp.yes', 'RSVP: YES')}
                                        </Tag>
                                    )}
                                    {player.rsvpChoice === 'MAYBE' && (
                                        <Tag color="gold" icon={<QuestionCircleOutlined />}>
                                            {t('invitations.rsvp.maybe', 'RSVP: MAYBE')}
                                        </Tag>
                                    )}
                                    {player.rsvpChoice === 'NO' && (
                                        <Tag color="default" icon={<CloseCircleOutlined />}>
                                            {t('invitations.rsvp.no', 'RSVP: NO')}
                                        </Tag>
                                    )}
                                    {player.status === 'RSVPED' ? (
                                        <Tag color="success">{t('reservation.status.rsvped', 'RSVP Confirmed')}</Tag>
                                    ) : player.status === 'RESERVED' ? (
                                        <Tag color="blue">{t('reservation.status.reserved', 'Reserved')}</Tag>
                                    ) : (
                                        <Tag>{player.status}</Tag>
                                    )}
                                </Space>
                            </Flex>

                            {player.playerNote && (
                                <div style={{
                                    marginTop: 10,
                                    padding: '8px 12px',
                                    background: '#f9f9f9',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    color: '#555'
                                }}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                                        {t('joinRequests.detail.playerNote', 'Player Note')}:
                                    </Text>
                                    {player.playerNote}
                                </div>
                            )}
                        </List.Item>
                    )}
                />
            )}
        </Modal>
    );
};
